import { Box, Skeleton, Stack } from "@mui/material";
import viewSignRequestStyles from "./ViewSignRequest.styles";
import viewSignRequestTokens from "./ViewSignRequest.tokens";

const styles = viewSignRequestStyles;
const { sectionHeader, recipientRow, audit } = viewSignRequestTokens;

interface ViewSignRequestSkeletonProps {
  isDesktop: boolean;
}

/** Card chrome with the design's header block, matching the loaded cards. */
const SkeletonCard = ({
  withIcon = false,
  padded = false,
  children,
}: {
  withIcon?: boolean;
  padded?: boolean;
  children?: React.ReactNode;
}) => (
  <Box sx={styles.card}>
    <Box sx={withIcon ? styles.auditHeader : styles.sectionHeader()}>
      <Box sx={styles.sectionHeaderLead}>
        {withIcon && (
          <Skeleton
            variant="rounded"
            width={audit.iconTileSize}
            height={audit.iconTileSize}
          />
        )}
        <Skeleton variant="text" width={120} height={22} />
        <Skeleton
          variant="rounded"
          width={sectionHeader.countMinWidth}
          height={sectionHeader.countHeight}
          sx={{ borderRadius: sectionHeader.countRadius }}
        />
      </Box>
    </Box>
    {children && <Box sx={padded ? styles.auditBody : undefined}>{children}</Box>}
  </Box>
);

const RecipientRowSkeleton = () => (
  <Box sx={styles.recipientRow(false)}>
    <Skeleton
      variant="circular"
      width={recipientRow.avatarSize}
      height={recipientRow.avatarSize}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Skeleton variant="text" width="42%" height={18} />
      <Skeleton variant="text" width="58%" height={14} />
    </Box>
    <Skeleton variant="rounded" width={78} height={24} sx={{ borderRadius: 999 }} />
    <Skeleton
      variant="circular"
      width={recipientRow.kebabSize}
      height={recipientRow.kebabSize}
    />
  </Box>
);

const TimelineRowSkeleton = ({ last = false }: { last?: boolean }) => (
  <Box sx={styles.auditEvent}>
    <Box sx={styles.auditRail}>
      <Skeleton variant="circular" width={audit.railSize} height={audit.railSize} />
      {!last && <Box sx={styles.auditConnector} />}
    </Box>
    <Box sx={{ ...styles.auditContent, flex: 1 }}>
      <Skeleton variant="text" width="45%" height={16} />
      <Skeleton variant="text" width="80%" height={14} />
      <Skeleton variant="text" width="35%" height={14} />
    </Box>
  </Box>
);

/** Full-page loading skeleton mirroring the Sign Request Detail layout. */
const ViewSignRequestSkeleton: React.FC<ViewSignRequestSkeletonProps> = ({
  isDesktop,
}) => (
  // Below `md` the page drops its own top padding for the sticky bar, which the
  // skeleton stands in for — so it has to supply that inset itself.
  <Box aria-hidden="true" sx={{ pt: { xs: "14px", sm: "22px", md: 0 } }}>
    <Box sx={styles.breadcrumb}>
      <Skeleton variant="text" width={100} height={18} />
      <Skeleton variant="circular" width={4} height={4} />
      <Skeleton variant="text" width={200} height={18} />
    </Box>

    <Box sx={styles.headerCard}>
      <Box sx={styles.headerRow}>
        <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: "13px" }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Skeleton variant="text" width="45%" height={30} />
            <Skeleton variant="rounded" width={96} height={24} sx={{ borderRadius: 999 }} />
          </Stack>
          <Stack direction="row" spacing={2.5} sx={{ mt: 1.25 }} flexWrap="wrap">
            <Skeleton variant="text" width={150} height={18} />
            <Skeleton variant="text" width={130} height={18} />
            <Skeleton variant="text" width={120} height={18} />
          </Stack>
        </Box>
        {isDesktop && (
          <Stack direction="row" spacing={1.25} flexShrink={0}>
            <Skeleton variant="rounded" width={190} height={44} sx={{ borderRadius: "10px" }} />
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: "10px" }} />
          </Stack>
        )}
      </Box>
      <Box sx={styles.progressStrip}>
        <Skeleton variant="text" width={130} height={16} />
        <Skeleton variant="rounded" height={7} sx={{ flex: 1, borderRadius: "4px" }} />
        <Skeleton variant="text" width={90} height={16} />
      </Box>
    </Box>

    <Box sx={styles.columns}>
      <Box sx={styles.column}>
        <SkeletonCard>
          {[0, 1].map((i) => (
            <Box key={i} sx={styles.documentRow}>
              <Skeleton variant="rounded" width={42} height={52} sx={{ borderRadius: "7px" }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="52%" height={18} />
                <Skeleton variant="text" width="30%" height={14} />
              </Box>
              <Skeleton variant="rounded" width={92} height={36} sx={{ borderRadius: "9px" }} />
              <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: "9px" }} />
            </Box>
          ))}
        </SkeletonCard>
        <SkeletonCard>
          {[0, 1, 2].map((i) => (
            <RecipientRowSkeleton key={i} />
          ))}
        </SkeletonCard>
      </Box>

      <Box sx={styles.column}>
        <SkeletonCard withIcon padded>
          {[0, 1, 2, 3].map((i) => (
            <TimelineRowSkeleton key={i} last={i === 3} />
          ))}
        </SkeletonCard>
        {/* Request details — hidden for now, mirrors the loaded page.
        <Box sx={styles.detailsCard}>
          <Skeleton variant="text" width={130} height={22} sx={{ mb: details.titleMarginBottom }} />
          <Box sx={styles.detailsRows}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Box key={i} sx={styles.detailsRow}>
                <Skeleton variant="text" width={100} height={16} />
                <Skeleton variant="text" width={90} height={16} />
              </Box>
            ))}
          </Box>
        </Box> */}
      </Box>
    </Box>
  </Box>
);

export default ViewSignRequestSkeleton;
